import { Grid, Typography } from "@mui/material";
import Selector from "components/select/selector";
import { ActivityBiomassScreening } from "types/firestoreFeatures";
import { beautifyNumber } from "utils/numberModification";
import { useTranslation } from "utils/locales/utilityFunctions";
import {
  AGRICULTURE_AND_NONFOREST_INTERVENTIONS,
  BIOMASS_CATEGORIES,
  FOREST_INTERVENTIONS,
  hasClearcutLikeIntervention,
} from "../options";

type BiomassScreeningInterventionFieldProps = {
  data: ActivityBiomassScreening;
  inputDisabled: boolean;
  derivedActivityData?: {
    totalArea: number;
    largestArea: number;
  };
  gridColumns?: number;
  setData: (change: { value: string[]; key: "activity"; parent: "standAndActivity" }) => void;
};

const BiomassScreeningInterventionField = ({
  data,
  inputDisabled,
  derivedActivityData,
  gridColumns = 4,
  setData,
}: BiomassScreeningInterventionFieldProps) => {
  const { translate } = useTranslation();
  const activityValue = data?.standAndActivity?.activity as string[] | string | undefined;
  const selectedActivities =
    typeof activityValue === "string" ? [activityValue] : activityValue ?? [];
  const isForest = data?.category === BIOMASS_CATEGORIES[1];
  const isAgricultureOrNonforest =
    data?.category === BIOMASS_CATEGORIES[0] || data?.category === BIOMASS_CATEGORIES[4];
  const selectableActivities = selectedActivities.map((activity) => {
    if (activity === "fellingAndThinning") return "thinning";
    return activity;
  });
  const interventionIds = isAgricultureOrNonforest
    ? AGRICULTURE_AND_NONFOREST_INTERVENTIONS
    : FOREST_INTERVENTIONS;
  const interventionOptions = interventionIds.map((id) => ({
    id,
    label: translate(`biomass-project-form-harvest-${id}`),
  }));

  return (
    <Grid item xs={12} sm={gridColumns}>
      <Typography color="secondary" sx={{ mb: 1 }}>
        {translate("biomass-project-form-harvest-category")}
      </Typography>
      <Selector
        title={translate("biomass-project-form-harvest-category")}
        value={selectableActivities}
        options={interventionOptions}
        label={true}
        fullWidth={true}
        changeHandler={(event) => {
          const value = event.target.value;
          setData({
            value: Array.isArray(value) ? value : value.split(","),
            key: "activity",
            parent: "standAndActivity",
          });
        }}
        disabled={data?.type === "clearcutEnergyWood" || inputDisabled}
        required={true}
        multiple={true}
        sx={{ mb: 1 }}
      />
      {isForest && hasClearcutLikeIntervention(selectedActivities) && derivedActivityData && (
        <>
          <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: "pre-line" }}>
            {translate("biomass-project-form-harvest-area-info-total") +
              ": " +
              beautifyNumber(derivedActivityData.totalArea, 2) +
              " ha\n"}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {translate("biomass-project-form-harvest-area-info-largest") +
              ": " +
              beautifyNumber(derivedActivityData.largestArea, 2) +
              " ha"}
          </Typography>
        </>
      )}
    </Grid>
  );
};

export default BiomassScreeningInterventionField;
